import { GlBadge, GlButton, GlTooltipDirective } from '@gitlab/ui';
import { mapActions, mapState } from 'pinia';
import { createAlert } from '~/alert';
import { useBatchComments } from '~/batch_comments/store';
import SafeHtml from '~/vue_shared/directives/safe_html';
import { renderGFM } from '~/behaviors/markdown/render_gfm';
import { confirmAction } from '~/lib/utils/confirm_via_gl_modal/confirm_via_gl_modal';
import { IMAGE_DIFF_POSITION_TYPE } from '~/diffs/constants';
import { sprintf, __ } from '~/locale';
import NoteForm from '~/notes/components/note_form.vue';
import { useNotes } from '~/notes/store/legacy_notes';
import { updateNoteErrorMessage } from '~/notes/utils';
import LineRangeHeadline from '~/rapid_diffs/app/discussions/line_range_headline.vue';
import resolvedStatusMixin from '../mixins/resolved_status';
export default {
    name: 'PreviewItem',
    components: {
        GlBadge,
        GlButton,
        LineRangeHeadline,
        NoteForm
    },
    directives: {
        GlTooltip: GlTooltipDirective,
        SafeHtml
    },
    mixins: [
        resolvedStatusMixin
    ],
    props: {
        draft: {
            type: Object,
            required: true
        }
    },
    emits: [
        'click'
    ],
    data () {
        return {
            isEditing: false,
            isDeleting: false
        };
    },
    computed: {
        ...mapState(useNotes, [
            'getDiscussion'
        ]),
        discussion () {
            return this.getDiscussion(this.draft.discussion_id);
        },
        isDiffDiscussion () {
            return this.discussion && this.discussion.diff_discussion;
        },
        filePath () {
            const file = this.discussion ? this.discussion.diff_file : this.draft;
            return file?.file_path;
        },
        threadTitle () {
            return sprintf(__("Reply to %{authorsName}'s thread"), {
                authorsName: this.discussion.notes.find((note)=>!note.system).author.name
            }, false);
        },
        showLinePosition () {
            return this.draft.file_hash || this.isDiffDiscussion;
        },
        position () {
            return this.draft.position || this.discussion.position;
        },
        imagePositionText () {
            if (this.position?.position_type !== IMAGE_DIFF_POSITION_TYPE) return null;
            return sprintf(__('Comment on image at %{x}x %{y}y'), this.position);
        },
        saveButtonTitle () {
            return this.draft.internal ? __('Save internal note') : __('Save comment');
        },
        publishErrorTitle () {
            if (!this.draft.publish_errors?.length) return null;
            return sprintf(__('This comment could not be published: %{reasons}'), {
                reasons: this.draft.publish_errors.join(', ')
            }, false);
        }
    },
    watch: {
        'draft.note_html': {
            async handler () {
                await this.$nextTick();
                renderGFM(this.$refs.noteBody);
            },
            immediate: true
        }
    },
    methods: {
        ...mapActions(useBatchComments, [
            'updateDraft',
            'deleteDraft'
        ]),
        async onDelete () {
            const confirmed = await confirmAction(__('Are you sure you want to delete this pending comment?'), {
                primaryBtnVariant: 'danger',
                primaryBtnText: __('Delete comment')
            });
            if (!confirmed) return;
            this.isDeleting = true;
            await this.deleteDraft(this.draft);
            this.isDeleting = false;
        },
        // eslint-disable-next-line max-params
        async onFormUpdate (noteText, parentElement, callback, resolveDiscussion) {
            try {
                await this.updateDraft({
                    note: this.draft,
                    noteText,
                    resolveDiscussion
                });
                this.stopEditing();
            } catch (error) {
                createAlert({
                    message: updateNoteErrorMessage(error),
                    parent: this.$el,
                    captureError: true,
                    error
                });
                callback();
            }
        },
        async onFormCancel (shouldConfirm, isDirty) {
            if (shouldConfirm && isDirty) {
                const confirmed = await confirmAction(sprintf(__('Are you sure you want to cancel editing this %{commentType}?'), {
                    commentType: this.draft.internal ? __('internal note') : __('comment')
                }), {
                    primaryBtnText: __('Cancel editing'),
                    primaryBtnVariant: 'danger',
                    secondaryBtnVariant: 'default',
                    secondaryBtnText: __('Continue editing'),
                    hideCancel: true
                });
                if (!confirmed) return;
            }
            this.stopEditing();
        },
        async stopEditing () {
            this.isEditing = false;
            await this.$nextTick();
            this.$refs.editButton.$el.focus();
        }
    },
    showStaysResolved: false,
    safeHtmlConfig: {
        ADD_TAGS: [
            'use',
            'gl-emoji',
            'copy-code'
        ]
    }
};
